import FeelingTool from "../components/FeelingTool";
import FourVerbs from "../components/FourVerbs";
import { CHARLIE } from "../lib/api";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";

const USE_CASES = [
  { k: "At home", body: "A bedtime or after-school read-aloud for children ages 4–8 navigating parental absence, estrangement, complicated grief, or a hard transition. Written to be read together — a grown-up's steady voice, a child's real questions, and a book that lets both be there without rushing to a tidy answer." },
  { k: "In the counseling office", body: "A softer opening for the sessions where the hard thing hasn't been named yet. Each book will include clinician notes and discussion prompts, so it can sit inside work with children in family transition, complicated grief, or attachment repair — without asking a child to lead with words they don't yet have." },
  { k: "In the classroom", body: "A read-aloud aligned with early-elementary SEL objectives around emotional literacy, self-advocacy, identifying trusted adults, and community. A free educator's guide (K–3, with age-adapted discussion prompts) will be shared with waitlist members before launch." },
  { k: "In advocacy work", body: "A shared-language tool for guardians ad litem, family advocates, and social workers meeting a child mid-chapter — including CASA volunteers, family court services, child protective services caseworkers, and child life specialists in hospital settings. Small enough to slip in a bag; steady enough to open in a hallway. Meant to give a child something to point at when words are hard to find." },
];

const BRING_CHARLIE = [
  {
    label: "For counselors and therapists",
    body: "Join the waitlist to receive the clinician's guide before launch and be first to know about bulk-order pricing for practices and clinics.",
    cta: { to: "/waitlist", text: "Join the waitlist", type: "link" },
  },
  {
    label: "For teachers and school counselors",
    body: "The free educator's guide (K–3, with age-adapted discussion prompts) arrives in waitlist inboxes before launch. Bulk orders for classroom sets, school libraries, and district-wide reads are available; contact us to discuss.",
    cta: { to: "/waitlist", text: "Join the waitlist", type: "link" },
  },
  {
    label: "For advocates and social workers",
    body: "We keep a small reserve for guardians ad litem, family advocates, CASA volunteers, and hospital child life specialists. Reach out and we'll send options for individual and organizational orders.",
    cta: { to: "mailto:hello@lovewellproject.com?subject=Advocacy%20&%20social%20work%20orders", text: "Contact us", type: "mail" },
  },
];

export default function Parents() {
  return (
    <>
      <section className="pt-32 md:pt-40 pb-8 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-4">For the grown-ups reading alongside</div>
            <h1 className="serif text-[clamp(3rem,7vw,6.5rem)] leading-[0.98] tracking-tight text-espresso">
              A soft hand, on a <span className="italic text-[#4A7C7B]">hard day.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-charcoal/90">
              The Love Well Project is written for the parent holding steady, and for the professionals holding space alongside them — therapists, counselors, teachers, school social workers, and advocates who want kinder, more accurate language for the tender chapters of a childhood.
            </p>
          </div>
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="torn-frame overflow-hidden"
            >
              <img src={CHARLIE.parentReading} alt="A parent reading to a child" className="w-full h-[380px] object-cover" />
            </motion.div>
            <div className="italic-serif text-xs text-charcoal/55 mt-4">
              Illustrations by Zuzana Svobodová
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-8">Where these books belong</div>
          <ul className="grid md:grid-cols-2 gap-x-14 gap-y-14">
            {USE_CASES.map((u, i) => (
              <motion.li
                key={u.k}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                className="grid grid-cols-[auto_1fr] gap-8 items-start"
              >
                <div className="chapter-number text-[4rem] md:text-[6rem] leading-none">0{i+1}</div>
                <div>
                  <h3 className="serif text-2xl md:text-3xl text-espresso">{u.k}</h3>
                  <p className="mt-4 text-charcoal/90 leading-relaxed max-w-md">{u.body}</p>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      <FourVerbs variant="full" />

      {/* How to bring Charlie into your work */}
      <section data-testid="bring-charlie" className="py-24 md:py-32 bg-[#F0EBE1]/60 border-y border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-6 items-end mb-14">
            <div className="md:col-span-8">
              <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-4">If you'd like to bring this into your work</div>
              <h2 className="serif text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.02] tracking-tight text-espresso">
                How to bring Charlie <span className="italic text-[#4A7C7B]">into your work.</span>
              </h2>
            </div>
            <div className="md:col-span-4 text-charcoal/90 text-sm md:text-base leading-relaxed">
              Three quiet paths for professionals who'd like the books in the rooms they hold space in.
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {BRING_CHARLIE.map((b, idx) => (
              <motion.div
                key={b.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.7, delay: idx * 0.05 }}
                data-testid={`bring-card-${idx}`}
                className="torn-frame bg-[#FFFDF9] p-8 md:p-10 flex flex-col justify-between min-h-[320px]"
              >
                <div>
                  <div className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-3">0{idx+1}</div>
                  <h3 className="serif text-2xl leading-[1.15] text-espresso">{b.label}</h3>
                  <p className="mt-4 text-sm text-charcoal/90 leading-relaxed">{b.body}</p>
                </div>
                <div className="mt-8">
                  {b.cta.type === "mail" ? (
                    <a
                      href={b.cta.to}
                      data-testid={`bring-cta-${idx}`}
                      className="group inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-2.5 text-sm hover:bg-[#2C241B] hover:text-[#FAF8F5] transition-colors"
                    >
                      <Mail size={14} /> {b.cta.text}
                    </a>
                  ) : (
                    <Link
                      to={b.cta.to}
                      data-testid={`bring-cta-${idx}`}
                      className="group inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-2.5 text-sm hover:bg-[#2C241B] hover:text-[#FAF8F5] transition-colors"
                    >
                      {b.cta.text}
                      <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FeelingTool />
    </>
  );
}
